import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { Loader2, ShoppingBag, ArrowLeft, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { useCurrency } from "@/context/CurrencyContext";

/**
 * Public read-only Plyx shop for a single creator.
 *   /plyx/u/:handle/shop
 *
 * Renders a 2-col grid of product cards. Each card deep-links to the
 * existing AfriMarket listing page so checkout flows through the
 * already-battle-tested Stripe pipeline.
 */
const PlyxShop = () => {
  const { handle } = useParams();
  const [creator, setCreator] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { formatPriceDual } = useCurrency();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await plyxApi.get(`/plyx/u/${handle}/shop`);
        if (!alive) return;
        setCreator(data.creator);
        setProducts(data.products || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Shop not found");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [handle]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center" data-testid="plyx-shop-loading">
        <Loader2 className="w-6 h-6 animate-spin opacity-60"/>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-shop-page">
      <div className="max-w-3xl mx-auto px-5 pt-6 pb-24">
        <Link to={`/plyx/u/${handle}`} className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white mb-4" data-testid="plyx-shop-back">
          <ArrowLeft className="w-4 h-4"/> back to @{handle}
        </Link>
        <div className="flex items-center gap-4 mb-6">
          {creator?.avatar_url ? (
            <img src={plyxMediaUrl(creator.avatar_url)} alt={creator.display_name} className="w-14 h-14 rounded-full object-cover ring-2 ring-[#FFB42D]"/>
          ) : (
            <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] flex items-center justify-center font-bold text-2xl">
              {(creator?.display_name || creator?.handle || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div>
            <div className="font-display text-2xl tracking-tight">{creator?.display_name || creator?.handle}&apos;s Shop</div>
            <div className="text-xs text-white/50 mt-0.5 inline-flex items-center gap-2">
              <ShoppingBag className="w-3 h-3 text-[#FFB42D]"/>
              {products.length} product{products.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        {products.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center" data-testid="plyx-shop-empty">
            <ShoppingBag className="w-10 h-10 mx-auto text-white/30 mb-3"/>
            <div className="font-semibold">No products yet</div>
            <div className="text-xs text-white/50 mt-1">@{handle} hasn&apos;t added anything to their Plyx shop.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {products.map((p) => (
              <ProductCard key={p.listing_id} product={p} formatPriceDual={formatPriceDual}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ProductCard = ({ product: p, formatPriceDual }) => {
  const [busy, setBusy] = useState(false);
  const dual = formatPriceDual(p.price, p.currency);
  const soldOut = p.stock <= 0;

  const buyNow = async () => {
    if (soldOut || busy) return;
    setBusy(true);
    try {
      const { data } = await plyxApi.post("/plyx/shop/checkout", {
        listing_id: p.listing_id,
        qty: 1,
        success_url: `${window.location.origin}/plyx/order/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: window.location.href,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Checkout couldn't open");
        setBusy(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start checkout");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`plyx-shop-product-${p.listing_id}`}
      className="group rounded-2xl overflow-hidden border border-white/10 bg-white/5 hover:border-[#FF2D7A] transition-all flex flex-col"
    >
      <div className="aspect-square bg-black/50 overflow-hidden">
        {p.thumbnail_url ? (
          <img src={p.thumbnail_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20">
            <ShoppingBag className="w-10 h-10"/>
          </div>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="text-sm font-semibold line-clamp-2 leading-tight mb-2" title={p.title}>{p.title}</div>
        <div className="mt-auto">
          <div className="font-display text-lg text-[#FFB42D] tabular-nums truncate" data-testid={`plyx-shop-price-${p.listing_id}`}>
            {dual.primary}
          </div>
          {dual.isConverted && dual.original && (
            <div className="text-[10px] text-white/40 tabular-nums mb-2" data-testid={`plyx-shop-price-original-${p.listing_id}`}>
              ≈ {dual.original}
            </div>
          )}
          {soldOut ? (
            <div className="mt-2 text-center text-[10px] text-red-400 uppercase tracking-wider py-2 rounded-md bg-red-500/10 border border-red-500/20">
              Sold out
            </div>
          ) : (
            <button
              type="button"
              onClick={buyNow}
              disabled={busy}
              data-testid={`plyx-shop-buy-${p.listing_id}`}
              className="mt-2 w-full h-9 rounded-full text-xs font-semibold text-black inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
              style={{ background: "#E85D4E" }}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <ShoppingCart className="w-3.5 h-3.5"/>}
              {busy ? "Opening…" : "Buy now"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlyxShop;
